import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import RoomRestrictionBadge from "./RoomRestrictionBadge";
import { InternalJobMetaContent } from "./InternalJobMeta";
import {
  SHOW_TASK_TIME_CHIPS,
  formatDateTime,
  formatDuration,
  formatPartsUsedForInput,
  formatTaskStatusLabel,
  formatTimeLabel,
  getRoomNameColor,
  getStatusColor,
  getTaskStableKey,
  getTaskTitle,
  isHighPriority,
  isNonRoutineTask,
  isTerminalTaskStatus,
  normalizeTaskStatus,
  shouldPromptFailureReason,
  toText,
} from "./servicesUtils";

// Small tab label with a tick shown when that note field has content.
function NotesTabLabel({ label, hasContent }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <span>{label}</span>
      {hasContent ? (
        <CheckCircleRoundedIcon color="success" sx={{ fontSize: 14 }} />
      ) : null}
    </Stack>
  );
}

// Top-level "Details"/"Notes" tab header - same look as DetailsNotesTabs
// used on the job list card.
function TopTabHeader({ label, active, onClick }) {
  return (
    <Typography
      onClick={onClick}
      variant="body2"
      color={active ? "primary.main" : "text.secondary"}
      sx={{
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: "pointer",
        userSelect: "none",
        textDecoration: active ? "underline" : "none",
      }}
    >
      {label}
    </Typography>
  );
}

// Composer + newest-first list for one note type (job or internal), with
// inline per-entry editing. Entries are never reordered on edit - only the
// create time determines position, so edits don't jump an entry to the top.
function NotesPanel({ entries, onAdd, onUpdate, saving, currentUserId }) {
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  const sorted = [...(entries || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const handleAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    await onAdd(trimmed);
    setDraft("");
  };

  const handleStartEdit = (entry) => {
    setEditingId(entry.id);
    setEditDraft(entry.content);
  };

  const handleSaveEdit = async (entryId) => {
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    await onUpdate(entryId, trimmed);
    setEditingId(null);
    setEditDraft("");
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Add a note…"
          fullWidth
          size="small"
          multiline
          minRows={1}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.9rem" } }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={handleAdd}
          disabled={saving || !draft.trim()}
        >
          Add
        </Button>
      </Stack>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notes yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {sorted.map((entry) => {
            const isEditing = editingId === entry.id;
            const canEdit =
              String(entry.created_by_id ?? "") === String(currentUserId ?? "");
            return (
              <Box
                key={entry.id}
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {isEditing ? (
                  <Stack spacing={0.5}>
                    <TextField
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      autoFocus
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button size="small" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveEdit(entry.id)}
                        disabled={saving || !editDraft.trim()}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {entry.content}
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {entry.created_by_name || "Unknown"} ·{" "}
                        {formatDateTime(entry.created_at)}
                        {entry.updated_at ? " (edited)" : ""}
                      </Typography>
                      {canEdit ? (
                        <Button
                          size="small"
                          onClick={() => handleStartEdit(entry)}
                        >
                          Edit
                        </Button>
                      ) : null}
                    </Stack>
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// Presentational job-detail drawer for My Services. All state/business logic
// (saving, task status transitions, timers) lives in the parent page; this
// component only renders the panel and reports actions back via callbacks.
export default function ServicesJobDetailDrawer({
  isMobile,
  open,
  onClose,
  onDrawerPullStart,
  onDrawerPullMove,
  onDrawerPullEnd,
  selectedJob,
  panelError,
  saving,
  jobElapsedSeconds,
  selectedTaskProgress,
  selectedTaskBreakdown,
  selectedJobTotalMinutes,
  taskStatusFilter,
  onTaskStatusFilterChange,
  selectedTasks,
  expandedTaskKeys,
  taskDrafts,
  animatingTaskKeys,
  taskFailureHighlightKeys,
  failureInputRefs,
  language,
  viewMode = "technician",
  t,
  onStartJob,
  onCompleteAllTasksAndJob,
  onReopenJob,
  showFailJobInline,
  onToggleFailJobInline,
  onFailJob,
  failJobReason,
  onFailJobReasonChange,
  failJobReasonTouched,
  onToggleTaskExpanded,
  onUpdateTaskDraft,
  onTaskStatusChange,
  onAddNote,
  onUpdateNote,
  currentUserId,
}) {
  const isInternalView = viewMode === "internal";
  const [activeTopTab, setActiveTopTab] = useState(null); // null | "details" | "notes"
  const [notesTab, setNotesTab] = useState("job");

  const toggleTopTab = (tab) => {
    setActiveTopTab((prev) => (prev === tab ? null : tab));
  };

  const jobNoteEntries = selectedJob?.job_notes_entries || [];
  const internalNoteEntries = selectedJob?.internal_notes_entries || [];
  const hasJobNotes = jobNoteEntries.length > 0;
  const hasInternalNotes = internalNoteEntries.length > 0;
  const hasFailureReason = Boolean(
    String(selectedJob?.cancellation_reason || "").trim(),
  );
  const activeNotesTab =
    notesTab === "internal" && !isInternalView ? "job" : notesTab;

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      ModalProps={{
        disableRestoreFocus: true,
        disableAutoFocus: true,
      }}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "60vw",
          height: isMobile ? "92vh" : "100%",
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
          p: 1.25,
          overscrollBehaviorY: "contain",
          touchAction: "pan-y",
        },
      }}
    >
      {selectedJob ? (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {isMobile ? (
            <Box
              onTouchStart={onDrawerPullStart}
              onTouchMove={onDrawerPullMove}
              onTouchEnd={onDrawerPullEnd}
              onTouchCancel={onDrawerPullEnd}
              sx={{
                width: 44,
                height: 5,
                borderRadius: 999,
                bgcolor: "divider",
                mx: "auto",
                mt: 0.25,
                mb: 0.75,
              }}
            />
          ) : null}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 0.5 }}
          >
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <RoomRestrictionBadge
                  restriction={selectedJob?.room_restriction}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: getRoomNameColor(selectedJob?.room_restriction),
                  }}
                >
                  {selectedJob.room_name}
                </Typography>
                {isHighPriority(selectedJob?.priority) ? (
                  <Chip
                    size="small"
                    label={t("highPriority")}
                    color="error"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                  />
                ) : null}
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {selectedJob.job_number || t("noJob")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                paddingLeft={1.5}
              >
                {selectedJob.device_name ||
                  selectedJob.device_id ||
                  t("noDevice")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {toText(selectedJob.status) === "completed" ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onReopenJob}
                  disabled={saving}
                >
                  {t("reopenJob") || "Reopen"}
                </Button>
              ) : null}
              <Chip
                size="small"
                label={selectedJob.status || "unknown"}
                color={getStatusColor(selectedJob.status)}
              />
            </Stack>
          </Stack>

          <Box sx={{ px: 1 }}>
            <Stack direction="row" spacing={2.5} alignItems="center">
              {isInternalView ? (
                <TopTabHeader
                  label="Details"
                  active={activeTopTab === "details"}
                  onClick={() => toggleTopTab("details")}
                />
              ) : null}
              <TopTabHeader
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Notes</span>
                    {hasJobNotes || hasInternalNotes || hasFailureReason ? (
                      <CheckCircleRoundedIcon
                        color="success"
                        sx={{ fontSize: 14 }}
                      />
                    ) : null}
                  </Stack>
                }
                active={activeTopTab === "notes"}
                onClick={() => toggleTopTab("notes")}
              />
            </Stack>

            <Collapse in={activeTopTab !== null} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 0.75 }}>
                {activeTopTab === "details" ? (
                  <InternalJobMetaContent job={selectedJob} />
                ) : null}

                {activeTopTab === "notes" ? (
                  <>
                    <Tabs
                      value={activeNotesTab}
                      onChange={(_, v) => setNotesTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: 36,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Tab
                        value="job"
                        label={
                          <NotesTabLabel
                            label="Job notes"
                            hasContent={hasJobNotes}
                          />
                        }
                        sx={{ minHeight: 36, py: 0.5, fontSize: "0.85rem" }}
                      />
                      {isInternalView ? (
                        <Tab
                          value="internal"
                          label={
                            <NotesTabLabel
                              label="Internal notes"
                              hasContent={hasInternalNotes}
                            />
                          }
                          sx={{ minHeight: 36, py: 0.5, fontSize: "0.85rem" }}
                        />
                      ) : null}
                      <Tab
                        value="failure"
                        label={
                          <NotesTabLabel
                            label="Failure Reason"
                            hasContent={hasFailureReason}
                          />
                        }
                        sx={{ minHeight: 36, py: 0.5, fontSize: "0.85rem" }}
                      />
                    </Tabs>
                    {activeNotesTab === "job" ? (
                      <NotesPanel
                        entries={jobNoteEntries}
                        onAdd={(content) => onAddNote("job", content)}
                        onUpdate={(entryId, content) =>
                          onUpdateNote("job", entryId, content)
                        }
                        saving={saving}
                        currentUserId={currentUserId}
                      />
                    ) : null}
                    {activeNotesTab === "internal" ? (
                      <NotesPanel
                        entries={internalNoteEntries}
                        onAdd={(content) => onAddNote("internal", content)}
                        onUpdate={(entryId, content) =>
                          onUpdateNote("internal", entryId, content)
                        }
                        saving={saving}
                        currentUserId={currentUserId}
                      />
                    ) : null}
                    {activeNotesTab === "failure" ? (
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="body2"
                          color={
                            hasFailureReason ? "text.primary" : "text.secondary"
                          }
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {selectedJob?.cancellation_reason ||
                            "No failure reason recorded."}
                        </Typography>
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Box>
            </Collapse>
          </Box>

          {panelError ? (
            <Alert severity="warning" sx={{ mx: 1, mt: 0.5 }}>
              {panelError}
            </Alert>
          ) : null}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, pt: 1, pb: 1.2, flexWrap: "wrap", rowGap: 0.5 }}
          >
            {toText(selectedJob.status) === "in_progress" ? (
              <Chip
                size="medium"
                label={`⏱ ${formatDuration(Math.floor(jobElapsedSeconds / 60))} ${t("elapsed")}`}
                color="primary"
                variant="outlined"
                sx={{ fontSize: "0.9rem", height: 30 }}
              />
            ) : (
              <Button
                size="medium"
                variant="contained"
                sx={{ fontSize: "0.95rem", px: 1.6 }}
                onClick={onStartJob}
                disabled={
                  saving ||
                  [
                    "in_progress",
                    "in progress",
                    "completed",
                    "failed",
                  ].includes(toText(selectedJob.status))
                }
              >
                {t("startJob")}
              </Button>
            )}
            <Button
              size="medium"
              variant="outlined"
              color="error"
              sx={{ fontSize: "0.95rem", px: 1.6 }}
              onClick={showFailJobInline ? onFailJob : onToggleFailJobInline}
              disabled={
                saving ||
                ["completed", "failed", "cancelled"].includes(
                  toText(selectedJob.status),
                )
              }
            >
              {showFailJobInline ? t("confirmFailJob") : t("failJob")}
            </Button>
            <Chip
              size="medium"
              label={`${selectedTaskProgress.done}/${selectedTaskProgress.total} ${t("done")}`}
              variant="outlined"
              sx={{ fontSize: "0.9rem", height: 30 }}
            />
            {["completed", "failed"].includes(toText(selectedJob.status)) ? (
              <Chip
                size="medium"
                icon={<AccessTimeOutlinedIcon />}
                label={
                  selectedJobTotalMinutes == null
                    ? "0 min"
                    : formatDuration(selectedJobTotalMinutes) || "0 min"
                }
                variant="outlined"
                sx={{ fontSize: "0.9rem", height: 30 }}
              />
            ) : null}
          </Stack>

          {selectedTaskBreakdown.total > 0 ? (
            <Box sx={{ px: 1, pb: 1.1 }}>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 999,
                  overflow: "hidden",
                  bgcolor: "#e6ecf2",
                  display: "flex",
                }}
              >
                {selectedTaskBreakdown.completedPct > 0 ? (
                  <Box
                    sx={{
                      width: `${selectedTaskBreakdown.completedPct}%`,
                      bgcolor: "success.main",
                      transition: "width 450ms ease",
                    }}
                  />
                ) : null}
                {selectedTaskBreakdown.failedPct > 0 ? (
                  <Box
                    sx={{
                      width: `${selectedTaskBreakdown.failedPct}%`,
                      bgcolor: "error.main",
                      transition: "width 450ms ease",
                    }}
                  />
                ) : null}
                {selectedTaskBreakdown.skippedOrNaPct > 0 ? (
                  <Box
                    sx={{
                      width: `${selectedTaskBreakdown.skippedOrNaPct}%`,
                      bgcolor: "info.main",
                      transition: "width 450ms ease",
                    }}
                  />
                ) : null}
              </Box>
            </Box>
          ) : null}

          <Divider />

          {showFailJobInline ? (
            <Box sx={{ px: 1, pt: 1, pb: 1 }}>
              <TextField
                label={t("jobFailureReason")}
                placeholder={t("jobFailureReasonPlaceholder")}
                value={failJobReason}
                onChange={(event) => onFailJobReasonChange(event.target.value)}
                error={
                  failJobReasonTouched && !String(failJobReason || "").trim()
                }
                helperText={
                  failJobReasonTouched && !String(failJobReason || "").trim()
                    ? t("jobFailureReasonHint")
                    : ""
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                autoFocus
                sx={{
                  "& .MuiInputBase-input": { fontSize: "1rem" },
                  "& .MuiInputLabel-root": { fontSize: "1rem" },
                  "& .MuiFormHelperText-root": { fontSize: "0.9rem" },
                }}
              />
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{ mt: 0.75 }}
              >
                <Button
                  size="medium"
                  sx={{ fontSize: "0.95rem" }}
                  onClick={onToggleFailJobInline}
                  disabled={saving}
                >
                  {t("cancel")}
                </Button>
              </Stack>
            </Box>
          ) : null}

          <Box
            sx={{
              "& .MuiTabs-root": {
                minHeight: 40,
                borderBottom: 1,
                borderColor: "divider",
              },
            }}
          >
            <Tabs
              value={taskStatusFilter}
              onChange={(_, v) => onTaskStatusFilterChange(v)}
              variant="fullWidth"
              sx={{ minHeight: 40, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab
                value="todo"
                label={t("taskFilter.todo")}
                sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
              />
              <Tab
                value="finished"
                label={t("taskFilter.finished")}
                sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
              />
              <Tab
                value="all"
                label={t("taskFilter.all")}
                sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
              />
            </Tabs>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overscrollBehaviorY: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {selectedTasks.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "1rem" }}
                >
                  {t("noTasksConfigured")}
                </Typography>
              </Box>
            ) : (
              <List sx={{ px: 1 }}>
                {selectedTasks
                  .filter((task) => {
                    const done = isTerminalTaskStatus(task?.status);
                    if (taskStatusFilter === "todo") return !done;
                    if (taskStatusFilter === "finished") return done;
                    return true;
                  })
                  .map((task, index) => {
                    // Keep original index so status-change callbacks still address the right task
                    const originalIndex = selectedTasks.indexOf(task);
                    const index_ = originalIndex >= 0 ? originalIndex : index;
                    const taskStatus = normalizeTaskStatus(task?.status);
                    const stableKey = getTaskStableKey(task, index_);
                    const detailsOpen = Boolean(expandedTaskKeys[stableKey]);
                    const draft = taskDrafts[stableKey] || {
                      technician_notes: task?.technician_notes || "",
                      failure_reason: task?.failure_reason || "",
                      parts_used: formatPartsUsedForInput(task?.parts_used),
                    };
                    const isAnimatingOut = Boolean(
                      animatingTaskKeys[stableKey],
                    );
                    const showFailureHint =
                      shouldPromptFailureReason(taskStatus) &&
                      !String(draft.failure_reason || "").trim();
                    const highlightFailureField =
                      Boolean(taskFailureHighlightKeys[stableKey]) &&
                      shouldPromptFailureReason(taskStatus) &&
                      !String(draft.failure_reason || "").trim();
                    const isInProgress = taskStatus === "in_progress";
                    const isDone = isTerminalTaskStatus(taskStatus);

                    return (
                      <Box
                        key={`${selectedJob.pk_id}_${task.pk_id || index_}`}
                        sx={{
                          overflow: "hidden",
                          display: "grid",
                          gridTemplateRows: isAnimatingOut ? "0fr" : "1fr",
                          opacity: isAnimatingOut ? 0 : 1,
                          transition:
                            "grid-template-rows 1000ms ease, opacity 1000ms ease",
                        }}
                      >
                        <Box sx={{ minHeight: 0 }}>
                          <ListItem
                            sx={{
                              px: 0.4,
                              py: 1.2,
                              alignItems: "flex-start",
                              transform: isAnimatingOut
                                ? "translateX(44px)"
                                : "translateX(0)",
                              opacity: isAnimatingOut ? 0 : 1,
                              transition:
                                "transform 1000ms ease, opacity 1000ms ease",
                            }}
                          >
                            <Box sx={{ width: "100%" }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 700, fontSize: "1.22rem" }}
                                >
                                  {getTaskTitle(task, index, language)}
                                </Typography>
                                {isNonRoutineTask(task) ? (
                                  <Chip
                                    size="small"
                                    label={t("new")}
                                    color="error"
                                    sx={{ height: 22, fontSize: "0.78rem" }}
                                  />
                                ) : null}
                              </Stack>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.8}
                                sx={{ mt: 0.35 }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="span"
                                  sx={{ fontSize: "1.05rem" }}
                                >
                                  {formatTaskStatusLabel(taskStatus)}
                                </Typography>
                                {isAnimatingOut ? (
                                  <CheckCircleRoundedIcon
                                    color="primary"
                                    sx={{ fontSize: 18 }}
                                  />
                                ) : null}
                              </Stack>

                              {SHOW_TASK_TIME_CHIPS && task?.started_at ? (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{
                                    mt: 0.6,
                                    flexWrap: "wrap",
                                    rowGap: 0.4,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={`▶ ${formatTimeLabel(task.started_at)}`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: "0.7rem", height: 22 }}
                                  />
                                  {task.time_spent_minutes != null ? (
                                    <Chip
                                      size="small"
                                      label={`⏱ ${formatDuration(task.time_spent_minutes)}`}
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem", height: 22 }}
                                    />
                                  ) : isInProgress ? (
                                    <Chip
                                      size="small"
                                      label="⏱ running…"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem", height: 22 }}
                                    />
                                  ) : null}
                                  {task?.completed_at ? (
                                    <Chip
                                      size="small"
                                      label={`■ ${formatTimeLabel(task.completed_at)}`}
                                      color="success"
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem", height: 22 }}
                                    />
                                  ) : null}
                                </Stack>
                              ) : null}

                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1.2}
                                sx={{ mt: 0.9 }}
                              >
                                <Button
                                  size="medium"
                                  variant="text"
                                  sx={{ fontSize: "1.02rem", fontWeight: 600 }}
                                  endIcon={
                                    <ExpandMoreOutlinedIcon
                                      sx={{
                                        transform: detailsOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 150ms ease",
                                      }}
                                    />
                                  }
                                  onClick={() =>
                                    onToggleTaskExpanded(task, index_)
                                  }
                                >
                                  {t("details")}
                                </Button>

                                {!isDone ? (
                                  <Stack
                                    direction="row"
                                    alignItems="flex-end"
                                    spacing={1}
                                  >
                                    <Button
                                      size="small"
                                      variant={
                                        taskStatus === "failed"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      color="error"
                                      sx={{
                                        minWidth: 78,
                                        minHeight: 38,
                                        maxHeight: 38,
                                        manWidth: 78,
                                        fontWeight: 700,
                                        fontSize: "0.9rem",
                                      }}
                                      onClick={() =>
                                        onTaskStatusChange(index_, "failed")
                                      }
                                      disabled={
                                        saving || isDone || isAnimatingOut
                                      }
                                    >
                                      {t("fail")}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={
                                        taskStatus === "not_applicable"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      sx={{
                                        minWidth: 78,
                                        minHeight: 38,
                                        maxHeight: 38,
                                        maxWidth: 78,
                                        fontWeight: 700,
                                        fontSize: "0.9rem",
                                      }}
                                      onClick={() =>
                                        onTaskStatusChange(
                                          index_,
                                          "not_applicable",
                                        )
                                      }
                                      disabled={
                                        saving || isDone || isAnimatingOut
                                      }
                                    >
                                      {t("notApplicable")}
                                    </Button>
                                    <Button
                                      size="medium"
                                      variant={
                                        taskStatus === "completed"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      sx={{
                                        minWidth: 142,
                                        height: 56,
                                        minHeight: 56,
                                        px: 2.6,
                                        py: 1.25,
                                        fontWeight: 700,
                                        fontSize: "1.08rem",
                                        lineHeight: 1,
                                      }}
                                      onClick={() =>
                                        onTaskStatusChange(index_, "completed")
                                      }
                                      disabled={
                                        saving || isDone || isAnimatingOut
                                      }
                                    >
                                      {t("complete")}
                                    </Button>
                                  </Stack>
                                ) : null}
                              </Stack>

                              <Collapse
                                in={detailsOpen}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                  {task?.task_description ||
                                  task?.task_description_dk ? (
                                    <Alert
                                      severity="info"
                                      sx={{
                                        py: 0.25,
                                        "& .MuiAlert-message": {
                                          fontSize: "1rem",
                                        },
                                      }}
                                    >
                                      {language === "da" &&
                                      task?.task_description_dk
                                        ? task.task_description_dk
                                        : task.task_description}
                                    </Alert>
                                  ) : null}

                                  {showFailureHint ? (
                                    <Alert
                                      severity={
                                        highlightFailureField
                                          ? "error"
                                          : "warning"
                                      }
                                      sx={{
                                        py: 0.25,
                                        "& .MuiAlert-message": {
                                          fontSize: "1rem",
                                        },
                                      }}
                                    >
                                      {t("taskFailureHint").replace(
                                        "{status}",
                                        formatTaskStatusLabel(taskStatus),
                                      )}
                                    </Alert>
                                  ) : null}

                                  <TextField
                                    label={t("technicianNotes")}
                                    value={draft.technician_notes}
                                    onChange={(event) =>
                                      onUpdateTaskDraft(
                                        task,
                                        index,
                                        "technician_notes",
                                        event.target.value,
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "1rem",
                                      },
                                      "& .MuiInputLabel-root": {
                                        fontSize: "1rem",
                                      },
                                    }}
                                  />

                                  <TextField
                                    label={t("failureNote")}
                                    placeholder={t("failureNotePlaceholder")}
                                    value={draft.failure_reason}
                                    inputRef={(element) => {
                                      if (element) {
                                        failureInputRefs.current[stableKey] =
                                          element;
                                      } else {
                                        delete failureInputRefs.current[
                                          stableKey
                                        ];
                                      }
                                    }}
                                    error={highlightFailureField}
                                    helperText={
                                      highlightFailureField
                                        ? t("failureNoteRequired")
                                        : ""
                                    }
                                    onChange={(event) =>
                                      onUpdateTaskDraft(
                                        task,
                                        index,
                                        "failure_reason",
                                        event.target.value,
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "1rem",
                                      },
                                      "& .MuiInputLabel-root": {
                                        fontSize: "1rem",
                                      },
                                      "& .MuiFormHelperText-root": {
                                        fontSize: "0.9rem",
                                      },
                                    }}
                                  />

                                  {taskStatus === "completed" ||
                                  isInProgress ? (
                                    <TextField
                                      label={t("partsUsed")}
                                      placeholder={t("partsUsedPlaceholder")}
                                      value={draft.parts_used}
                                      onChange={(event) =>
                                        onUpdateTaskDraft(
                                          task,
                                          index,
                                          "parts_used",
                                          event.target.value,
                                        )
                                      }
                                      fullWidth
                                      size="small"
                                      multiline
                                      minRows={2}
                                      sx={{
                                        "& .MuiInputBase-input": {
                                          fontSize: "1rem",
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: "1rem",
                                        },
                                      }}
                                    />
                                  ) : null}

                                  <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      size="medium"
                                      variant="outlined"
                                      sx={{ fontSize: "0.95rem" }}
                                      onClick={() =>
                                        onTaskStatusChange(index_, null)
                                      }
                                      disabled={saving}
                                    >
                                      {t("saveNotes")}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Collapse>
                            </Box>
                          </ListItem>
                          <Divider />
                        </Box>
                      </Box>
                    );
                  })}
              </List>
            )}
          </Box>
          {selectedTasks.length > 0 &&
          !["completed", "failed", "cancelled"].includes(
            toText(selectedJob.status),
          ) ? (
            <Stack
              direction="row"
              justifyContent="center"
              sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider" }}
            >
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={onCompleteAllTasksAndJob}
                disabled={saving}
              >
                {t("completeJobButton") || "Complete job"}
              </Button>
            </Stack>
          ) : null}

          <Box sx={{ pb: isMobile ? 2 : 0.5 }} />
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("selectJobPrompt")}
          </Typography>
        </Box>
      )}
    </Drawer>
  );
}
